import {
  BarController,
  type BarControllerChartOptions,
  type BarControllerDatasetOptions,
  type BarElement,
  type BarParsedData,
  type CartesianScaleTypeRegistry,
  type Chart,
  type ChartMeta,
  type ChartType,
  type Plugin,
  type Scale,
} from 'chart.js';
import { defaultChartFontFamily } from '@/components/charts/chartFont';
import { configuredOr } from '@/lib/configValue';

/**
 * Verbatim port of legacy `components/charts/barChartUtils.ts` — the pentagon bar controller
 * and the threshold-line plugin.
 *
 * **This file is raw canvas geometry and it is ported unchanged on purpose.** No test can
 * catch a regression in it (there is no assertion that says "the peaked cap is 5px tall and
 * the value label sits 8px above it") and the output is a client-visible chart, so the rule
 * for editing it is: change nothing that a Chart.js 4 API difference does not force, and
 * record every forced change. The three that were forced are marked `[chart.js 4]` below and
 * listed in `docs/reports/SYM-496.md`.
 *
 * Two legacy defects are visible here and both are **preserved**:
 *
 * - `ctx.font = fontSize + 'rem …'` — canvas `ctx.font` does not accept `rem`, so the
 *   assignment is rejected and whatever font Chart.js last set stays in effect
 *   (docs/MIGRATION-WIDGETS.md §8, W-6). Converting to px would change what is on the wall.
 * - `ctx.save()` without a matching `restore()` in `horizontalLine`, and one `restore()` too
 *   many per bar in the controller. The two are on the same canvas state stack and the
 *   controller's surplus pops outweigh the plugin's surplus push on any chart with more than
 *   one bar, so nothing accumulates; `restore()` on an empty stack is a no-op by spec.
 */

/* ----------------------------------- Type surface --------------------------------------- */

/** `chartConfig.horizontalLine.label`. */
export type LabelHorizontalLine = {
  borderRadius?: number;
  labelText: string | null;
  fontSize: number | null;
  fontColor: string | null;
  fontFamily: string | null;
  borderWidth: number | null;
  textOffset: number | null;
  borderColor: string | null;
  backgroundColor: string | null;
};

/** `chartConfig.horizontalLine` — the safety-line threshold and its label box. */
export type HorizontaLine = {
  /** Optional so the legacy `!== undefined` guard stays a real check, not dead code. */
  lineAt?: number;
  lineColor: string | null;
  label?: LabelHorizontalLine | null;
};

/** `chartConfig.barTickOption` — bar cap height, gradient fill and the value label font. */
export type BarTickOption = {
  display: boolean;
  fontFamily: string;
  fontSize: number;
  fontColor: string;
  gradientFrom?: string;
  gradientTo?: string;
  shapeHeight: number;
};

declare module 'chart.js' {
  /**
   * `SigangeBarController` [sic] is registered under its own chart type, exactly as the legacy
   * app did (`SigangeBarController.id = 'SigangeBarController'`). Teaching the registry about
   * it is what lets `<Chart type="SigangeBarController">` type-check without a cast; the shape
   * is `bar`'s, because the controller is a `BarController` subclass that only overrides
   * `draw()`.
   */
  // Module augmentation requires `interface`; a `type` cannot merge with an existing one.
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface ChartTypeRegistry {
    SigangeBarController: {
      chartOptions: BarControllerChartOptions;
      datasetOptions: BarControllerDatasetOptions;
      defaultDataPoint: number | [number, number] | null;
      metaExtensions: object;
      parsedDataType: BarParsedData;
      scales: keyof CartesianScaleTypeRegistry;
    };
  }

  /**
   * [chart.js 4] **Forced change #1.** Legacy read `chart.config._config.barTickOption` — a
   * private field holding a *custom top-level key* on the chart config object, which the
   * legacy code could set because it called `new Chart(canvas, config)` itself.
   *
   * `react-chartjs-2` builds that config internally from exactly `{ type, data, options,
   * plugins }`, so there is no longer any way to put a top-level key on it, private field or
   * not. The option moves into the one place Chart.js has for third-party option blocks —
   * `options.plugins.<id>` — and is declared here so it stays typed end to end.
   */
  // The type parameter is fixed by the interface being augmented and is unused in this member;
  // module augmentation also requires `interface` rather than `type`.
  /* eslint-disable-next-line @typescript-eslint/no-unused-vars,
     @typescript-eslint/consistent-type-definitions */
  interface PluginOptionsByType<TType extends ChartType> {
    signageBarTick: BarTickOption;
  }
}

/* ------------------------------- horizontalLine plugin ---------------------------------- */

/**
 * `afterDraw` plugin painting the threshold line plus its rounded, bordered label box.
 *
 * Geometry unchanged from the legacy file, including the manual `quadraticCurveTo` corners
 * and the `movex` fudge that squares off the bottom-left corner when `borderRadius` is 0
 * (which is what all four deployed configs set).
 */
export function horizontalLine(horizontalLine: HorizontaLine): Plugin<'SigangeBarController'> {
  return {
    id: 'horizontal-line',
    afterDraw(chart: Chart<'SigangeBarController'>) {
      if (horizontalLine.lineAt !== undefined) {
        let lineAt = horizontalLine.lineAt;
        // [chart.js 4] Forced change #2, cosmetic half: legacy destructured
        // `chartArea: { top, width, height }` here and never used any of the three.
        const { ctx } = chart;
        ctx.save();

        const radius =
          horizontalLine.label?.borderRadius !== undefined && horizontalLine.label.labelText
            ? horizontalLine.label.borderRadius
            : 0;

        // [chart.js 4] **Forced change #2.** Legacy looked the scales up as
        // `chart.scales[chart.config.options.scales.x.id]`. In Chart.js 4 `chart.config.options`
        // is the *raw user config*, and this app's config supplies `scales.x` with no `id`
        // key at all — so that expression is `chart.scales[undefined]`, i.e. `undefined`, and
        // the next line would throw. The dataset meta carries the resolved scale objects,
        // which is both the public API and exactly what the legacy expression was reaching for.
        const { xScale, yScale } = chart.getDatasetMeta(0);
        if (!xScale || !yScale) return;
        const xAxe: Scale = xScale;
        const yAxe: Scale = yScale;

        if (yAxe.min != 0) return;
        ctx.strokeStyle = configuredOr(horizontalLine.lineColor, 'white');
        ctx.beginPath();
        lineAt = (lineAt - yAxe.min) * (100 / yAxe.max);
        lineAt = ((100 - lineAt) / 100) * yAxe.height + yAxe.top;
        ctx.moveTo(xAxe.left, lineAt);
        ctx.lineTo(xAxe.right - radius - 2, lineAt);
        ctx.stroke();

        // label
        if (horizontalLine.label?.labelText) {
          const labelPadding: number = configuredOr(horizontalLine.label.textOffset, 5);
          const borderWidth: number = configuredOr(horizontalLine.label.borderWidth, 1);

          // [W-6] `'1.4rem'` is not a valid canvas font size, so this assignment is rejected
          // and the label inherits whatever font Chart.js last set. Preserved deliberately.
          const fontSize = horizontalLine.label.fontSize
            ? String(horizontalLine.label.fontSize) + 'rem'
            : '1rem';
          const fontFamily = configuredOr(horizontalLine.label.fontFamily, defaultChartFontFamily());
          const fontColor = configuredOr(horizontalLine.label.fontColor, 'white');
          ctx.font = fontSize + ' ' + fontFamily;
          ctx.textAlign = 'right';

          const textMetric: TextMetrics = ctx.measureText(horizontalLine.label.labelText);
          const labelHeight: number =
            textMetric.fontBoundingBoxAscent + 2 * (labelPadding + borderWidth);

          ctx.fillStyle = configuredOr(horizontalLine.label.backgroundColor, 'grey');
          ctx.lineWidth = borderWidth;
          ctx.strokeStyle = configuredOr(horizontalLine.label.borderColor, 'white');

          const labelX =
            xAxe.right - borderWidth - (textMetric.width + 2 * (labelPadding + borderWidth));
          const labelY = lineAt - borderWidth / 2 + 1;
          const labelX1 = labelX + (textMetric.width + 2 * (labelPadding + borderWidth));
          const labelY1 = lineAt - labelHeight;

          ctx.beginPath();
          ctx.moveTo(labelX + radius, labelY);
          ctx.lineTo(labelX1 - radius, labelY);
          ctx.quadraticCurveTo(labelX1, labelY, labelX1, labelY - radius);
          ctx.lineTo(labelX1, labelY1 + radius);
          ctx.quadraticCurveTo(labelX1, labelY1, labelX1 - radius, labelY1);
          ctx.lineTo(labelX + radius, labelY1);
          ctx.quadraticCurveTo(labelX, labelY1, labelX, labelY1 + radius);
          ctx.lineTo(labelX, labelY - radius);
          const movex = radius === 0 ? borderWidth : 0;
          ctx.quadraticCurveTo(labelX + movex, labelY, labelX + radius, labelY);
          ctx.fill('evenodd');
          ctx.stroke();
          ctx.fillStyle = fontColor;
          const halfFontSize =
            (textMetric.fontBoundingBoxAscent - textMetric.fontBoundingBoxDescent) / 2;
          ctx.fillText(
            horizontalLine.label.labelText,
            xAxe.right - borderWidth - (labelPadding + borderWidth),
            lineAt - (labelHeight / 2 - halfFontSize),
          );
        }
      }
    },
  };
}

/* ---------------------------------- SigangeBarController -------------------------------- */

/**
 * The legacy per-field truthiness defaults, unchanged.
 *
 * One deviation, and it is not a Chart.js one: the legacy function **mutated the object it
 * was handed**, writing its defaults back into the config node. Ours returns a new object.
 * The config object is shared by reference and re-read by SYM-492's slider, which is the same
 * reason W-11 exists for `imageLabelWidget`.
 */
function defaultbarTickOption(barTickOption: Partial<BarTickOption> | undefined): BarTickOption {
  if (!barTickOption) {
    return {
      display: true,
      fontFamily: defaultChartFontFamily(),
      fontSize: 0.7,
      fontColor: 'white',
      shapeHeight: 6,
    };
  }

  // Note the two different fallback tests, both legacy: `display` and `shapeHeight` are
  // `!== undefined`, so a configured `0` height means 0; the other three are truthiness, so a
  // configured `0` font size means 0.7rem.
  return {
    display: barTickOption.display ?? true,
    fontFamily: configuredOr(barTickOption.fontFamily, defaultChartFontFamily()),
    fontSize: configuredOr(barTickOption.fontSize, 0.7),
    fontColor: configuredOr(barTickOption.fontColor, 'white'),
    gradientFrom: barTickOption.gradientFrom,
    gradientTo: barTickOption.gradientTo,
    shapeHeight: barTickOption.shapeHeight ?? 6,
  };
}

/**
 * [sic] "Sigange". A `BarController` whose `draw()` paints pentagon-shaped bars — a flat-sided
 * bar with a peaked cap `shapeHeight` tall — an optional vertical gradient fill, and the raw
 * value printed 8px above each bar.
 *
 * The class name is the registered chart type string and therefore as immovable as a config
 * key: `Chart.register` keys the controller by it and `<Chart type=…>` looks it up by it.
 */
export class SigangeBarController extends BarController {
  override draw() {
    const ctx = this.chart.ctx;

    const barTickOption: BarTickOption = defaultbarTickOption(
      this.chart.options.plugins?.signageBarTick,
    );
    // `BarController` is declared without its element type parameter, so `getMeta().data` is
    // `Element<AnyObject, AnyObject>[]` and every property read off it would be `any`. Naming
    // the element type is what lets the geometry below be typed numbers rather than `any` —
    // the legacy file annotated the same variable `(element: any)`.
    const meta = this.getMeta() as ChartMeta<'bar', BarElement>;
    // [chart.js 4] **Forced change #3.** Legacy read the raw datum as `element.$context.raw`.
    // `$context` is an untyped internal that only exists once the element's options have been
    // resolved; `getDataset().data[i]` is the same value through the public API, which is what
    // lets this file drop its `any`s. A non-numeric datum (`null`, or the `[from, to]` pair of
    // a floating bar) is read as 0 — legacy fell through to `null`, and `y + null === y + 0`
    // for the cap and both are falsy for the value label, so the drawing is identical.
    const values = this.getDataset().data;

    meta.data.forEach((element, index) => {
      const { x, y, base, width, height } = element.getProps([
        'x',
        'y',
        'base',
        'width',
        'height',
      ]);
      // The `Number(...)` wrappers on `x`/`y` are the legacy ones and they are load-bearing
      // for the type, not only the value: Chart.js declares `Point.x`/`Point.y` as
      // `number | null`, and `Number(null)` is `0` — which is what the arithmetic below wants
      // for an element that has not been positioned. `height` and `width` are plain numbers,
      // so the legacy wrappers around those are dropped as the no-ops they are.
      const outer = {
        x: Number(x),
        y: Number(y),
        // [chart.js 4] `element.height !== NaN` is `true` for every value including `NaN`
        // itself, so the legacy `$animations.height._to` fallback was unreachable. Dropped.
        // `outer.h` is then never read either — the draw uses `element.base` — but it is kept
        // so this object still matches the legacy one field for field.
        h: height,
        w: width,
      };
      const eloptions = element.options;
      const rawValue = values[index];
      const value = typeof rawValue === 'number' ? rawValue : 0;
      let shapeH = barTickOption.shapeHeight;
      shapeH = value < shapeH ? value : shapeH;
      const offset = outer.w / 2;
      const bottom = base; // (outer.y + outer.h > element.base)? element.base : outer.y + outer.h;
      ctx.save();
      ctx.beginPath();
      ctx.lineWidth = 1;
      ctx.strokeStyle = eloptions.borderWidth === 0 ? 'transparent' : eloptions.borderColor;
      ctx.moveTo(outer.x - offset, outer.y + shapeH);
      ctx.lineTo(outer.x - offset, bottom);
      ctx.lineTo(outer.x + offset, bottom);
      ctx.lineTo(outer.x + outer.w - offset, outer.y + shapeH);
      ctx.lineTo(outer.x, outer.y);
      ctx.lineTo(outer.x - offset, outer.y + shapeH);
      ctx.stroke();

      //ctx.clip();

      // gradient color
      if (barTickOption.gradientFrom && barTickOption.gradientTo) {
        const background_1: CanvasGradient = ctx.createLinearGradient(0, bottom, 0, outer.y);
        background_1.addColorStop(0, barTickOption.gradientFrom);
        background_1.addColorStop(1, barTickOption.gradientTo);
        ctx.fillStyle = background_1;
      } else {
        ctx.fillStyle = eloptions.backgroundColor;
      }
      ctx.fill('evenodd');
      ctx.restore();
      // bar value over bar
      if (barTickOption.display && value) {
        // [W-6] Rejected by canvas because of the `rem`, exactly as in the legacy app.
        ctx.font = String(barTickOption.fontSize) + 'rem ' + barTickOption.fontFamily;
        ctx.textAlign = 'center';
        ctx.fillStyle = barTickOption.fontColor;
        ctx.fillText(String(value), outer.x, outer.y - 8);
        ctx.restore();
      }
    });
  }
}

/**
 * Legacy `barChart.vue` assigned these two statics before registering the controller, and both
 * are kept.
 *
 * `id` is the one that matters: it is what gives the subclass its own chart type instead of
 * shadowing `'bar'`. The `defaults` assignment is a no-op — static members follow the class
 * prototype chain, so `SigangeBarController.defaults` already *is* `BarController.defaults` —
 * and it is kept because it is what the legacy file does.
 *
 * `BarController.overrides` is not assigned across, and does not need to be: it is inherited
 * by the same mechanism, and `Chart.register` reads `item.overrides` through the chain. That
 * matters, because `overrides` is what declares `x` a category scale and `y` a linear one, and
 * this chart's config supplies `scales.x`/`scales.y` with no `type` of their own.
 */
SigangeBarController.id = 'SigangeBarController';
SigangeBarController.defaults = BarController.defaults;
